<template>
  <main class="relative flex flex-col h-full max-h-full overflow-hidden focus:outline-none">
    <div class="relative px-4 py-2 sm:flex sm:items-center sm:justify-between sm:px-6 lg:px-8">
      <div class="flex justify-between min-w-0 py-2 w-fit">
        <h1 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Admin") }}
        </h1>
        <nav class="flex justify-between w-full">
          <ol class="flex items-center flex-1 ml-3">
            <router-link to="/admin/integrations" as="li">
              <div class="flex items-center cursor-pointer group">
                <ChevronRight
                  class="flex-shrink-0 w-5 h-5 text-gray-400"
                  :class="[{ 'rotate-180': $store.state.portfolio.ui_options?.language == 'ar' }]"
                  aria-hidden="true" />
                <span class="ml-2 text-sm font-medium text-gray-500 capitalize group-hover:text-gray-700"> {{ $t("Integrations") }} </span>
              </div>
            </router-link>
            <li
              v-for="bread in $store.state.ui.admin_integrations_breadcrumb"
              :key="bread?.id || bread"
              :class="{ 'cursor-pointer': bread?.onClick || bread?.link }"
              @click="bread?.onClick ? bread.onClick() : void null">
              <template v-if="bread.link">
                <router-link :to="bread.link" class="flex items-center">
                  <ChevronRight class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
                  <span class="ml-2 text-sm font-medium text-gray-500 capitalize hover:text-gray-700">{{ bread.label }}</span>
                </router-link>
              </template>
              <div v-else class="flex items-center">
                <ChevronRight class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
                <div
                  :class="[
                    bread?.is_object_type_name ? 'text-lg' : '',
                    'ml-2 text-sm font-medium capitalize text-gray-500 hover:text-gray-700'
                  ]">
                  {{ bread?.label || bread }}
                </div>
              </div>
            </li>
          </ol>
        </nav>
      </div>

      <!-- CSV upload / breadcrumb -->
      <template v-if="$store.state.ui.admin_integrations_breadcrumb?.includes('Import-Spreadsheet')">
        <div v-if="spreadsheet_is_loaded || submitting_is_on" class="flex flex-row-reverse gap-2">
          <button
            v-if="spreadsheet_is_loaded"
            type="button"
            :disabled="submitting_is_on"
            class="inline-flex items-center justify-center px-2 py-2 text-base font-medium text-white border border-transparent rounded-md shadow-sm w-36 disabled:opacity-80 bg-blue-light hover:bg-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light"
            @click="$emitter.emit(ALL_EMITTERS.SUBMIT_MAPPING)">
            <RotateCw :class="[submitting_is_on ? 'animate-spin' : '']" class="w-5 h-5 mr-3 -ml-1" aria-hidden="true" />
            <span v-if="submitting_is_on"> {{ $t("Submitting...") }} </span>
            <span v-else> {{ $t("Submit") }} </span>
          </button>
          <button
            v-if="spreadsheet_is_loaded && $store.state.ui.admin_integrations_breadcrumb?.length == 1"
            class="w-max cancel-btn"
            @click="$emitter.emit(ALL_EMITTERS.CANCEL_CSV_UPLOAD)">
            {{ $t("Cancel") }}
          </button>
          <button
            v-if="spreadsheet_is_loaded && $store.state.ui.admin_integrations_breadcrumb?.length > 1"
            class="w-max cancel-btn"
            @click="
              () => {
                $emitter.emit(ALL_EMITTERS.OBJECT_TYPE_NAME, null)
              }
            ">
            {{ $t("Go back") }}
          </button>
        </div>
        <button
          v-else-if="!spreadsheet_is_loaded || spreadsheet_is_loaded"
          class="cancel-btn"
          @click="$router.replace('/admin/integrations')">
          {{ $t("Cancel") }}
        </button>
      </template>
    </div>

    <transition
      mode="out-in"
      appear
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform translate-x-10 opacity-0"
      enter-to-class="transform translate-x-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0">
      <keep-alive>
        <DataIntegration />
      </keep-alive>
    </transition>
  </main>
</template>

<script>
import { ChevronRight, RotateCw } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import DataIntegration from "@/views/dashboard/admin/integrations/data/Container.vue"

export default {
  name: "IntegrationsMainContainer",
  components: {
    DataIntegration,
    ChevronRight,
    RotateCw
  },
  data() {
    return {
      ALL_EMITTERS,
      spreadsheet_is_loaded: false,
      submitting_is_on: false
    }
  },
  created() {
    this.register_listeners()
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.SPREADSHEET_IS_LOADED_IN_ADMIN_CONTAINER, (status) => (this.spreadsheet_is_loaded = status))
      this.$emitter.on(ALL_EMITTERS.SUBMITTING_CSV_STARTED_IN_ADMIN_CONTAINER, () => (this.submitting_is_on = true))
      this.$emitter.on(ALL_EMITTERS.SUBMITTING_CSV_ENDED_IN_ADMIN_CONTAINER, () => (this.submitting_is_on = false))
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.SPREADSHEET_IS_LOADED_IN_ADMIN_CONTAINER)
      this.$emitter.off(ALL_EMITTERS.SUBMITTING_CSV_STARTED_IN_ADMIN_CONTAINER)
      this.$emitter.off(ALL_EMITTERS.SUBMITTING_CSV_ENDED_IN_ADMIN_CONTAINER)
    }
  }
}
</script>
